import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchRoundSwingLeaders, SwingResponse } from "../../services/RatingAnalysisService";
import Loading from "../../components/Loading";
import PlayerImage from "../../components/PlayerImage";
import { getLatestSeason, getDivisionsBySeason } from "../../services/MatchService";
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import styles from '../../styles/StatsView.module.css';

// Round Swing (Rating 3.0 metric) exists only from season 32 onwards.
const FIRST_R3_SEASON = 32;

const formatSwing = (value: number | null | undefined): string =>
    value != null ? `${value >= 0 ? '+' : ''}${value.toFixed(2)}%` : '–';

const StatsViewSwingPage = () => {
    const [loading, setLoading] = useState(true);
    const [topData, setTopData] = useState<SwingResponse | null>(null);
    const [bottomData, setBottomData] = useState<SwingResponse | null>(null);
    const [season, setSeason] = useState<number>(FIRST_R3_SEASON);
    const [division, setDivision] = useState<string>("all");
    const [displayCount, setDisplayCount] = useState<number>(10);
    const [availableSeasons, setAvailableSeasons] = useState<number[]>([]);
    const [availableDivisions, setAvailableDivisions] = useState<string[]>([]);

    const buildDivisionList = (divisions: string[]) => {
        const aggregateOptions: string[] = [];
        if (divisions.some((d: string) => d.startsWith('2div'))) aggregateOptions.push('2div');
        if (divisions.some((d: string) => d.startsWith('3div'))) aggregateOptions.push('3div');
        if (divisions.some((d: string) => d.startsWith('4div'))) aggregateOptions.push('4div');
        return ["all", ...aggregateOptions, ...divisions];
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const latestSeason = await getLatestSeason();
                setSeason(latestSeason);

                const seasons = [];
                for (let s = FIRST_R3_SEASON; s <= latestSeason; s++) {
                    seasons.push(s);
                }
                setAvailableSeasons(seasons.reverse());

                const divisions = await getDivisionsBySeason(latestSeason);
                setAvailableDivisions(buildDivisionList(divisions));
            } catch (error) {
                console.error("Error fetching initial data:", error);
            }
        };

        fetchInitialData();
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const [topResponse, bottomResponse] = await Promise.all([
                    fetchRoundSwingLeaders(season, division, true),
                    fetchRoundSwingLeaders(season, division, false)
                ]);
                setTopData(topResponse);
                setBottomData(bottomResponse);
            } catch (error) {
                console.error("Error loading round swing data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (season && division) {
            loadData();
        }
    }, [season, division]);

    useEffect(() => {
        const fetchDivisionsForSeason = async () => {
            try {
                const divisions = await getDivisionsBySeason(season);
                setAvailableDivisions(buildDivisionList(divisions));
            } catch (error) {
                console.error("Error fetching divisions:", error);
            }
        };

        if (season) {
            fetchDivisionsForSeason();
        }
    }, [season]);

    const formatDivision = (div: string) => {
        if (div === "all") return "Alle Divisioner";
        const divMap: { [key: string]: string } = {
            'ligaen': 'Ligaen',
            '1div': '1. Division',
            '2div': '2. Division (Alle)',
            '3div': '3. Division (Alle)',
            '4div': '4. Division (Alle)',
            '2divA': '2. Division A',
            '2divB': '2. Division B',
            '3divA': '3. Division A',
            '3divB': '3. Division B',
            '3divC': '3. Division C',
            '3divD': '3. Division D',
            '4divA': '4. Division A',
            '4divB': '4. Division B',
            '4divC': '4. Division C',
            '4divD': '4. Division D',
            '4divE': '4. Division E',
            '4divF': '4. Division F'
        };
        return divMap[div] || div;
    };

    const getRankClass = (index: number) => {
        switch (index) {
            case 0: return styles.rank1;
            case 1: return styles.rank2;
            case 2: return styles.rank3;
            default: return styles.rankOther;
        }
    };

    if (loading) {
        return <Loading />;
    }

    if (!topData || !bottomData) {
        return <div className="text-center mt-5 text-white">Ingen Round Swing data tilgængelig (kun sæson {FIRST_R3_SEASON}+)</div>;
    }

    const renderColumn = (
        data: SwingResponse,
        heading: string,
        subtitle: string,
        isTop: boolean
    ) => (
        <div className={styles.column}>
            <div className={styles.columnHeader}>
                <div className={`${styles.columnIcon} ${isTop ? styles.gainIcon : styles.lossIcon}`}>
                    {isTop ? <TrendingUp style={{ color: '#28a745' }} /> : <TrendingDown style={{ color: '#dc3545' }} />}
                </div>
                <div className={styles.columnTitle}>
                    <h3>{heading}</h3>
                    <div className={styles.columnSubtitle}>{subtitle}</div>
                </div>
            </div>
            <div className={styles.playerList}>
                {data.players.slice(0, displayCount).map((player, index) => (
                    <div key={player.steamId} className={`${styles.playerCard} ${isTop ? styles.gainCard : styles.lossCard}`}>
                        <div className={`${styles.rankBadge} ${getRankClass(index)}`}>
                            {index + 1}
                        </div>
                        <div className={styles.playerImage}>
                            <PlayerImage id={player.steamId} width={50} />
                        </div>
                        <div className={styles.playerInfo}>
                            <Link to={`/player/${player.steamId}`} className={styles.playerName}>
                                {player.playerName}
                            </Link>
                            <div className={styles.teamInfo}>
                                <span>{player.teamName}</span>
                                <span className={styles.divisionBadge}>{formatDivision(player.division)}</span>
                            </div>
                        </div>
                        <div className={styles.ratingStats}>
                            <div className={`${styles.changeValue} ${player.swingPct >= 0 ? styles.gainValue : styles.lossValue}`}>
                                {formatSwing(player.swingPct)}
                            </div>
                            <div className={styles.ratingValues}>
                                R3 {player.rating30.toFixed(2)}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className={styles.pageContainer}>
            <div className={styles.twitterCard}>
                {/* Compact Header */}
                <div className={styles.compactHeader}>
                    <div className={styles.headerContent}>
                        <div className={styles.titleSection}>
                            <h1 className={styles.mainTitle}>Round Swing</h1>
                            <div className={styles.subtitle}>{formatDivision(division)}</div>
                        </div>
                        <div className={styles.seasonBadge}>Sæson {season}</div>
                    </div>
                </div>

                {/* Split Layout */}
                <div className={styles.splitContainer}>
                    {renderColumn(topData, "Højeste Swing", "Vinder flest runder for holdet", true)}
                    {renderColumn(bottomData, "Laveste Swing", "Trækker holdets runde-chance ned", false)}
                </div>

                {/* Info Box */}
                <div className={styles.infoBox}>
                    <strong>Round Swing:</strong> Hvor meget en spiller ændrer holdets sandsynlighed for at
                    vinde runder (i procentpoint pr. runde). Det er kernen i Rating 3.0 — positiv swing betyder
                    du løfter holdets round-chance, negativ betyder du trækker den ned.
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <div className={styles.footerText}>
                        <span className={styles.highlight}>PowerStats.dk</span> • Avanceret CS2 Statistik • Sæson {season}
                    </div>
                </div>

                {/* Filter Controls - Hidden at bottom */}
                <div className={styles.filterControls}>
                    <div className={styles.filterBar}>
                        <select
                            className={styles.filterSelect}
                            value={season}
                            onChange={(e) => setSeason(Number(e.target.value))}
                        >
                            {availableSeasons.map(s => (
                                <option key={s} value={s}>Sæson {s}</option>
                            ))}
                        </select>
                        <select
                            className={styles.filterSelect}
                            value={division}
                            onChange={(e) => setDivision(e.target.value)}
                        >
                            {availableDivisions.map(div => (
                                <option key={div} value={div}>
                                    {formatDivision(div)}
                                </option>
                            ))}
                        </select>
                        <select
                            className={styles.filterSelect}
                            value={displayCount}
                            onChange={(e) => setDisplayCount(Number(e.target.value))}
                        >
                            <option value={5}>Vis Top 5</option>
                            <option value={10}>Vis Top 10</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StatsViewSwingPage;
